<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { Dialog, Button, IconHelp, Text, useNotification } from "@ui-kit";
  import { ResponseCode, itemSetZoneApi } from "@src/api";
  import { ProductZone } from "@src/components";
  import { useItemList } from "../logic";

  const { item_id, zone_id } = defineProps<{ item_id: string | null; zone_id: string | null }>();
  const emit = defineEmits<{ (e: "close"): void; (e: "click:link"): void }>();

  const notification = useNotification();
  const i18n = useI18n();
  const is_pending_set_zone = ref(false);
  const item_list = useItemList();
  const item = computed(() => item_list.state.map[item_id] ?? null);

  const handleClickLinkZoneBtn = async () => {
    if (!zone_id) return;

    is_pending_set_zone.value = true;

    const result = await itemSetZoneApi({ item_id: [item_id], zone_id });

    is_pending_set_zone.value = false;

    if (result.code === ResponseCode.OK) {
      emit("click:link");
    } else {
      notification.show({ title: i18n.t("Ошибка при прикреплении товара"), type: "error" });
    }
  };

  watch(
    () => item_id,
    async () => {
      if (!item_id) return;

      await item_list.fetch([item_id]);
    },
  );
</script>

<template>
  <Dialog
    :isOpen="item_id !== null && item !== null"
    :closeOnOutsideClick="false"
    :useTeleport="false"
    @close="emit('close')"
  >
    <div class="not-binding-dialog" data-testid="not-binding-dialog">
      <div class="not-binding-dialog__content">
        <IconHelp style="width: 56px; height: 56px" />
        <div class="not-binding-dialog__title">
          <Text font="body-bold-18">{{ $t("Привязать товар к зоне") }}?</Text>
          <Text font="body-regular-14">{{ $t("Товар пока не привязан ни к одной зоне") }}</Text>
        </div>
        <ProductZone :zones="[]" :img="item.image_url" :article="item.article" :isBlocked="item.is_blocked" />
      </div>
      <div class="not-binding-dialog__footer">
        <Button
          size="s"
          @click="handleClickLinkZoneBtn"
          data-testid="not-binding-dialog-link-btn"
          :isLoading="is_pending_set_zone"
        >
          {{ $t("Привязать к текущей зоне") }}
        </Button>
        <Button size="s" variant="secondary" @click="emit('close')" data-testid="not-binding-dialog-cancel-btn">
          {{ $t("Отменить") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .not-binding-dialog {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
    width: 328px;
  }

  .not-binding-dialog__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .not-binding-dialog__content :deep(.icon) path {
    stroke-width: 1px;
  }

  .not-binding-dialog :deep(.product-zone) {
    width: 100%;
    padding: 0;
  }

  .not-binding-dialog__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .not-binding-dialog__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .not-binding-dialog__footer :deep(button) {
    width: 100%;
    padding: 0;
  }
</style>
